/**
 * ui.js — controller for video-bg-remover.
 *
 * ONE state attribute drives everything. `#vbrApp[data-state]` moves through
 *   upload → edit → work → review → [roto] → done
 * and the stylesheet decides what each phase shows, including whether the
 * settings rail exists at all. The previous version toggled six sibling
 * <div>s from JS and had a class-name mismatch between the markup and the
 * code (`vbr-hidden` in the blade, `hidden` in the script) which meant the
 * live matte preview — the only thing to look at during the longest wait in
 * the tool — was never once displayed. An attribute cannot drift from itself.
 *
 * TWO ENGINES.
 *   'ai'     RobustVideoMatting. Handles ordinary footage; costs a 15 MB model
 *            download and seconds per frame, so it is never run speculatively.
 *   'chroma' Colour/luma key. Free and instant, so it previews LIVE while the
 *            sliders move — the person sees the cutout before committing to a
 *            pass instead of after it.
 *
 * WHAT IS SOLD (Фарид 2026-09-01): clip length (10 s → 60 s), export height
 * (720p → 1080p) and the ProRes 4444 target. All three are read from the
 * shared primitives — __toolLimit.durationCap and __qualityTier — never from
 * a number written here, so a tier change in config/tiers.php lands without a
 * code edit.
 */
(function (root) {
    'use strict';

    var SLUG = 'video-bg-remover';
    var T = {};
    var $ = function (id) { return document.getElementById(id); };

    /* Absolute ceiling of the in-browser pipeline, independent of any plan: the
       matte store holds one PNG per frame and the matting is seconds-per-frame,
       so this is a device limit, not a commercial one. The tier caps below are
       always ≤ this. */
    var HARD_MAX_DURATION = 60;

    var S = {
        engine: 'ai',
        chroma: null,                       // VbrChroma opts
        target: 'overlay',
        bgMode: 'transparent', bgColor: '#00b140',
        gradType: 'linear', gradAngle: 135, gradC1: '#ff6b6b', gradC2: '#4ecdc4',
        blurRadius: 12, bgImage: null,
        fps: 15, resHeight: 720, audioKeep: true,
        edge: { feather: 0, choke: 0 },
        trimStart: 0, trimEnd: 0, duration: 0,
        videoW: 0, videoH: 0, processW: 0, processH: 0,
        fileName: '', fileSize: 0, hasAudio: true,
        file: null, url: null, limitKey: '',
        srcFile: null,                       // the copy the matte pass read — the export reads it too
        resultBlob: null, resultUrl: null, resultExt: 'webm',
        clamped: [],
    };

    var player = null, resultPlayer = null;
    var abortCtrl = null;
    var reviewTimer = 0;
    var matteDone = false;
    var limitRun = null;
    var previewFrame = null;                // canvas of the current source frame
    var previewBusy = false, previewAgain = false;
    var durationWallShown = false;
    /* Auto-detection runs ONCE per clip. Re-running it on every seek would
       silently overwrite a colour the person picked by hand two seconds ago —
       an "assistant" that undoes your work is worse than none. */
    var autoKeyDone = false;
    var availabilityTries = 0;
    /* «Сохранить предмет» (keep.js): pick mode, the samples it reads, and
       whether an object is tracked. */
    var keep = { mode: false, busy: false, samplingP: null, sampledFor: '', label: 1 };

    /* ═══════════════════════════ helpers ═══════════════════════════ */

    function tr(k, repl) {
        var s = (T[k] != null && T[k] !== '') ? T[k] : k;
        if (repl) for (var r in repl) s = s.split(':' + r).join(repl[r]);
        return s;
    }
    function fmtTime(sec) {
        if (!isFinite(sec) || sec < 0) sec = 0;
        var m = Math.floor(sec / 60), s = Math.floor(sec % 60), d = Math.floor((sec % 1) * 10);
        return m + ':' + (s < 10 ? '0' : '') + s + '.' + d;
    }
    function fmtBytes(b) {
        if (b < 1024) return b + ' B';
        if (b < 1048576) return (b / 1024).toFixed(1) + ' KB';
        return (b / 1048576).toFixed(1) + ' MB';
    }
    function fmtETA(sec) {
        if (!sec || sec < 60) return Math.ceil(sec || 0) + ' ' + tr('seconds');
        var m = Math.floor(sec / 60), s = Math.ceil(sec % 60);
        return m + ':' + (s < 10 ? '0' : '') + s;
    }
    function ae(kind, data) {
        try { if (root.__aeFunnel) root.__aeFunnel(kind, data || {}); } catch (_) {}
    }
    function stage(name) { try { root.__stage && root.__stage(name); } catch (_) {} }

    /* Segmented / pressed-button groups. aria-pressed is the state — the same
       attribute __qualityTier reads to find the active chip, so the resolution
       group is gated by declaration and needs no bespoke code here. */
    function pressGroup(box, onPick) {
        if (!box) return;
        box.addEventListener('click', function (e) {
            var btn = e.target.closest('button[data-val],button[data-target],button[data-key],button[data-bg]');
            if (!btn || !box.contains(btn) || btn.disabled) return;
            var sibs = box.querySelectorAll('button');
            for (var i = 0; i < sibs.length; i++) sibs[i].setAttribute('aria-pressed', String(sibs[i] === btn));
            onPick(btn.dataset.val || btn.dataset.target || btn.dataset.key || btn.dataset.bg, btn);
        });
    }
    function press(box, value, attr) {
        if (!box) return;
        var sibs = box.querySelectorAll('button');
        for (var i = 0; i < sibs.length; i++) {
            sibs[i].setAttribute('aria-pressed', String(sibs[i].getAttribute(attr || 'data-val') === String(value)));
        }
    }

    /* ═══════════════════════════ state machine ═══════════════════════════ */

    function setState(name) {
        var cb = $('vbrCancel');
        if (cb && name !== 'work' && cb.disabled) { cb.disabled = false; cb.textContent = tr('cancel'); }
        var app = $('vbrApp');
        if (!app || app.dataset.state === name) { if (app) app.dataset.state = name; return; }
        var swap = function () { app.dataset.state = name; };
        // A cross-fade between phases makes the tool read as one surface rather
        // than six that replace each other. Purely additive: without the API the
        // swap is the instant one it always was.
        if (window.__viewTransition) {
            var clearName = function () { try { $('vbrStage').style.viewTransitionName = ''; } catch (_) {} };
            try { $('vbrStage').style.viewTransitionName = 'vbr-stage'; } catch (_) {}
            var t = window.__viewTransition(swap);
            if (t && t.finished) t.finished.catch(function () {}).then(clearName); else clearName();
            return;
        }
        swap();
    }
    function state() { return ($('vbrApp') || {}).dataset ? $('vbrApp').dataset.state : ''; }

    function setProgress(pct, text, sub) {
        var b = $('vbrBar'); if (b) b.style.width = pct + '%';
        var p = $('vbrPct'); if (p) p.textContent = pct > 0 ? Math.round(pct) + '%' : '';
        if (text != null) $('vbrWorkT').textContent = text;
        if (sub != null) $('vbrWorkS').textContent = sub;
    }

    /* ═══════════════════════════ tier ceilings ═══════════════════════════ */

    /** This viewer's clip-length ceiling, in seconds. */
    function durationCap() {
        var tier = 0;
        try { tier = (root.__toolLimit && root.__toolLimit.durationCap(SLUG)) || 0; } catch (_) {}
        return tier > 0 ? Math.min(tier, HARD_MAX_DURATION) : HARD_MAX_DURATION;
    }
    /** What Премиум would allow — read off the limit block, which renders it
        from config, so this file never states a number of its own. */
    function premiumDurationCap() {
        var el = document.querySelector('[data-tl-dur-premium]');
        var v = el ? parseInt(el.getAttribute('data-tl-dur-premium'), 10) : 0;
        return v > 0 ? Math.min(v, HARD_MAX_DURATION) : 0;
    }
    function isPro() {
        try { return !!(root.__toolLimit && root.__toolLimit.isPro && root.__toolLimit.isPro()); } catch (_) { return false; }
    }
    function maxHeight() {
        try {
            if (root.__qualityTier) {
                if (root.__qualityTier.isPro()) return 4320;
                var c = root.__qualityTier.videoMaxHeight();
                return (c === Infinity || !c) ? 4320 : c;
            }
        } catch (_) {}
        return 4320;
    }

    function renderDurationNote() {
        var el = $('vbrDurNote');
        if (!el) return;
        var cap = durationCap(), prem = premiumDurationCap();
        var sel = S.trimEnd - S.trimStart;
        var atCap = sel >= cap - 0.05;
        el.classList.toggle('is-capped', atCap);
        el.textContent = (!isPro() && prem > cap)
            ? tr('dur_capped', { cap: cap, paid: prem, plan: planName() })
            : tr('dur_free', { cap: cap });
    }
    function planName() {
        try { return (root.__qualityTier && root.__qualityTier.planName()) || 'Premium'; } catch (_) { return 'Premium'; }
    }

    /* ═══════════════════════════ upload ═══════════════════════════ */

    function setupUpload() {
        var zone = $('vbrUpload');
        if (zone) zone.addEventListener('tool-uploader:file', function (e) {
            var f = e.detail && e.detail.file;
            if (f) handleFile(f);
        });
        document.addEventListener('paste', function (e) {
            if (state() !== 'upload') return;
            var items = e.clipboardData && e.clipboardData.items;
            if (!items) return;
            for (var i = 0; i < items.length; i++) {
                if (items[i].type.indexOf('video') === 0) {
                    var f = items[i].getAsFile();
                    if (f) { handleFile(f); break; }
                }
            }
        });
        var chip = $('vbrDurChip');
        if (chip) chip.textContent = tr('dur_free', { cap: durationCap() });
    }

    function handleFile(file) {
        if (!file.type.match(/^video\//)) { root.__toast(tr('err_format'), 'error'); return; }
        if (root.__validateFileSize && !root.__validateFileSize(file, 'video')) return;

        S.file = file;
        S.srcFile = null;            // the matte pass settles its own copy of THIS file
        S.fileName = file.name;
        S.fileSize = file.size;
        /* Stamp the billing key ONCE, here. Deriving it from the live File on
           every press is what let a tool bill the same material twice after it
           rewrote the file in place; and a key is what makes "the same clip
           again today is free" true rather than merely printed. */
        S.limitKey = file.name + '|' + file.size + '|' + (file.lastModified || 0);

        releaseSource();

        setState('work');
        setProgress(8, tr('loading'), file.name + ' · ' + fmtBytes(file.size));
        $('vbrLive').hidden = true;
        stage('file_loaded');
        ae('file', { size_mb: Math.round(file.size / 1048576) });

        if (player) { player.destroy(); player = null; }
        var token = ++loadToken;
        openSource(file).then(function (src) {
            if (token !== loadToken) { releaseSource(src); return; }
            S.src = src;
            S.file = src.file;
            S.url = src.url;
            if (src.tier && src.tier !== 'native') {
                ae('source', { tier: src.tier, vcodec: src.vcodec || '', container: src.container || '', ms: src.ms || 0, scaled: src.scaledTo || 0 });
            }
            mountPlayer();
        }, function (e) {
            if (token !== loadToken) return;
            S.loading = false;
            if (e && e.step === 'cancel') { resetToUpload(); return; }
            var key = (e && e.step === 'no_video') ? 'err_format' : (e && e.step === 'engine') ? 'err_load' : 'err_decode';
            root.__toast(tr(key), 'error');
            ae('source_fail', { step: (e && e.step) || 'other', native_step: e && e.nativeStep, code: e && e.mediaCode,
                vcodec: e && e.vcodec, container: e && e.container, ext: String(file.name || '').split('.').pop().toLowerCase().slice(0, 8),
                mb: Math.round(file.size / 1048576), msg: String((e && e.message) || e).slice(0, 140) });
            resetToUpload();
        });
    }

    /* Any picked video → a source both readers of this tool can use: the
       player on screen and the frame extractor behind the matte pass.
       RoSource (/remove-object-from-video's loader) owns the ladder — durable
       copy of the pick, a real <video> judged by its DIMENSIONS (HEVC without
       a decoder, MPEG-4 Part 2 and ProRes report 0×0 with no error at all),
       then a remux or an H.264 transcode when the browser cannot show it.
       Before this the tool gave up at the first rung: «Не удаётся прочитать
       это видео» — 114 sessions on RU in two weeks, 94 of them .mp4. */
    var loadToken = 0;
    function openSource(file) {
        var R = root.RoSource, cfg = root.__vbrSourceCfg || {};
        if (!R || typeof R.open !== 'function') {
            return Promise.resolve({ file: file, url: URL.createObjectURL(file), tier: 'native', _urls: [] });
        }
        S.loading = true;
        var probe = document.createElement('video');
        probe.muted = true; probe.playsInline = true; probe.preload = 'metadata';
        return R.open(file, {
            video: probe, timeoutMs: 25000,
            ffmpegUrl: cfg.ffmpegUrl, ladderUrl: cfg.ladderUrl, codecMatrixUrl: cfg.codecMatrixUrl,
            onProgress: function (stageName, frac) {
                var pct = typeof frac === 'number' ? Math.round(frac * 100) : 0;
                if (stageName === 'probe') setProgress(8, tr('loading'), S.fileName);
                else setProgress(8 + Math.round(pct * 0.8), tr('loading_convert', { pct: pct }), tr('loading_convert_note'));
            }
        }).then(function (src) {
            S.loading = false;
            try { probe.removeAttribute('src'); probe.load(); if (probe.parentNode) probe.parentNode.removeChild(probe); } catch (_) {}
            return src;
        }, function (e) {
            try { probe.removeAttribute('src'); probe.load(); if (probe.parentNode) probe.parentNode.removeChild(probe); } catch (_) {}
            throw e;
        });
    }
    function releaseSource(src) {
        src = src || S.src;
        if (src && root.RoSource && RoSource.release) { try { RoSource.release(src); } catch (_) {} }
        else if (S.url) { try { URL.revokeObjectURL(S.url); } catch (_) {} }
        if (!arguments.length) { S.src = null; S.url = null; }
    }

    function mountPlayer() {
        var guard = setTimeout(function () {
            if (!S.duration) { root.__toast(tr('err_decode'), 'error'); resetToUpload(); }
        }, 15000);

        player = root.__createTimbricaPlayer($('vbrPlayerMount'), {
            speed: true, volume: true, fullscreen: true, maxHeight: 420,
            onReady: function (p) {
                clearTimeout(guard);
                S.duration = p.video.duration || 0;
                S.videoW = p.video.videoWidth || 0;
                S.videoH = p.video.videoHeight || 0;
                S.hasAudio = detectAudio(p.video);
                S.trimStart = 0;
                S.trimEnd = Math.min(S.duration, durationCap());
                setState('edit');
                stage('ready');
                buildThumbs();
                buildResChips();
                refreshTargetAvailability();
                updateTrim();
                applyTargetConstraints();
                refreshSummary();
                p.seek(0);
                grabFrame();
                // Scrubbing re-samples: the colour key previews the frame the
                // person is actually looking at, not the one they opened on.
                p.video.addEventListener('seeked', function () { grabFrame(); });
            },
            onTimeUpdate: function (t) { if (S.duration > 0) movePlayhead(t); },
        });
        player.load(S.url);
        player.video.onerror = function () { root.__toast(tr('err_decode'), 'error'); resetToUpload(); };
    }

    /* A video element cannot be asked "do you have audio?" portably; the
       vendor-prefixed track counts are the only signal that exists, and their
       ABSENCE must mean "assume yes" — muxing an audio stream that turns out to
       be empty is harmless, silently dropping one the person recorded is not. */
    function detectAudio(v) {
        try {
            if (typeof v.mozHasAudio === 'boolean') return v.mozHasAudio;
            if (v.audioTracks && typeof v.audioTracks.length === 'number') return v.audioTracks.length > 0;
            if (typeof v.webkitAudioDecodedByteCount === 'number' && v.currentTime > 0) return v.webkitAudioDecodedByteCount > 0;
        } catch (_) {}
        return true;
    }

    function resetToUpload() {
        if (abortCtrl) abortCtrl.abort();
        if (limitRun) { try { limitRun.abort(); } catch (_) {} limitRun = null; }
        stopReviewLoop();
        if (player) { player.destroy(); player = null; }
        if (resultPlayer) { resultPlayer.destroy(); resultPlayer = null; }
        if (root.RoSource && S.loading && RoSource.cancel) { try { RoSource.cancel(); } catch (_) {} }
        S.loading = false;
        loadToken++;
        releaseSource();
        if (S.resultUrl) { URL.revokeObjectURL(S.resultUrl); S.resultUrl = null; }
        S.resultBlob = null; S.file = null; S.srcFile = null; S.duration = 0; S.limitKey = '';
        matteDone = false; previewFrame = null; durationWallShown = false; autoKeyDone = false;
        keep.samplingP = null; keep.sampledFor = '';
        if (root.VbrKeep) { clearKeep(false); VbrKeep.reset(true); }
        slidersTouched = false;
        showCompare(false);
        try { VbrRoto.reset(); VbrMatteStore.clear(); } catch (_) {}
        var zone = $('vbrUpload');
        if (zone && zone.__uploaderReset) zone.__uploaderReset();
        setState('upload');
        stage('idle');
    }

    /* ═══════════════════════════ timeline ═══════════════════════════ */

    function buildThumbs() {
        var box = $('vbrThumbs');
        if (!box) return;
        box.innerHTML = '';
        var n = Math.min(20, Math.max(4, Math.ceil(S.duration)));
        var tw = 80, th = 48;
        var c = document.createElement('canvas');
        c.width = tw * n; c.height = th;
        box.appendChild(c);
        var ctx = c.getContext('2d');
        (async function () {
            if (typeof root.__videoThumbFallback !== 'function') return;
            var mobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
            try {
                var res = await root.__videoThumbFallback({
                    file: S.file, videoEl: null, duration: S.duration,
                    width: S.videoW || 1920, height: S.videoH || 1080,
                    count: n, isMobile: mobile,
                    /* No FFmpeg for a strip of 80-px thumbs on a phone: on this page
                       the engine is not loaded, so the lane only waited 15 s on its
                       stub and logged «module never loaded» (60 events on iPhones,
                       18.09–02.10); loaded, it would decode the whole clip once per
                       thumb in another wasm memory — the budget the matting net needs.
                       The strip stays empty; nothing else depends on it. */
                    noFfmpeg: mobile,
                    frameWidth: tw, slug: SLUG,
                });
                var frames = res.frames || [];
                if (!frames.length) return;
                var tmp = document.createElement('canvas');
                tmp.width = frames[0].imageData.width; tmp.height = frames[0].imageData.height;
                var tctx = tmp.getContext('2d');
                for (var i = 0; i < frames.length; i++) {
                    tctx.putImageData(frames[i].imageData, 0, 0);
                    ctx.drawImage(tmp, i * tw, 0, tw, th);
                }
            } catch (_) {}
        })();
    }

    function setupTrim() {
        var track = $('vbrTrack'), hl = $('vbrHandleL'), hr = $('vbrHandleR'), region = $('vbrRegion');
        if (!track) return;
        var drag = null, lastX = 0, startVal = 0;

        function timeAt(x) {
            var r = track.getBoundingClientRect();
            var p = (x - r.left) / r.width;
            if (getComputedStyle(track).direction === 'rtl') p = 1 - p;
            return Math.max(0, Math.min(1, p)) * S.duration;
        }
        function begin(kind) {
            return function (e) {
                drag = kind; lastX = e.clientX; startVal = S.trimStart;
                e.preventDefault();
                try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
            };
        }
        hl.addEventListener('pointerdown', begin('l'));
        hr.addEventListener('pointerdown', begin('r'));
        region.addEventListener('pointerdown', function (e) {
            if (e.target === hl || e.target === hr) return;
            begin('move')(e);
        });
        track.addEventListener('pointerdown', function (e) {
            // Click on empty track = scrub, the behaviour every editor has.
            if (e.target !== track && e.target !== $('vbrThumbs')) return;
            if (player) player.seek(timeAt(e.clientX));
        });

        document.addEventListener('pointermove', function (e) {
            if (!drag) return;
            var cap = durationCap(), t = timeAt(e.clientX), span = S.trimEnd - S.trimStart;
            if (drag === 'l') {
                S.trimStart = Math.max(0, Math.min(t, S.trimEnd - 0.3));
                if (S.trimEnd - S.trimStart > cap) { S.trimStart = S.trimEnd - cap; noteDurationWall(); }
            } else if (drag === 'r') {
                S.trimEnd = Math.max(S.trimStart + 0.3, Math.min(t, S.duration));
                if (S.trimEnd - S.trimStart > cap) { S.trimEnd = S.trimStart + cap; noteDurationWall(); }
            } else {
                var ns = startVal + (t - timeAt(lastX));
                ns = Math.max(0, Math.min(ns, S.duration - span));
                S.trimStart = ns; S.trimEnd = ns + span;
                lastX = e.clientX; startVal = S.trimStart;
            }
            updateTrim();
            if (player) player.seek(drag === 'r' ? S.trimEnd : S.trimStart);
        });
        document.addEventListener('pointerup', function () {
            if (drag) { drag = null; grabFrame(); }
        });
    }

    /* Hitting the ceiling is a fact worth reporting once, not on every pointer
       move: the handle simply stops, and without a word that reads as a broken
       control rather than a plan boundary. */
    function noteDurationWall() {
        if (durationWallShown) return;
        durationWallShown = true;
        var cap = durationCap(), prem = premiumDurationCap();
        ae('duration_wall', { cap_s: cap, premium_s: prem, sellable: (!isPro() && prem > cap) ? 1 : 0 });
        if (!isPro() && prem > cap) {
            root.__toast(tr('dur_capped', { cap: cap, paid: prem, plan: planName() }), 'info');
        }
    }

    function updateTrim() {
        if (!S.duration) return;
        var a = S.trimStart / S.duration * 100, b = S.trimEnd / S.duration * 100;
        $('vbrShadeL').style.width = a + '%';
        $('vbrShadeR').style.width = (100 - b) + '%';
        var r = $('vbrRegion');
        r.style.insetInlineStart = a + '%';
        r.style.width = (b - a) + '%';
        $('vbrTIn').textContent = fmtTime(S.trimStart);
        $('vbrTOut').textContent = fmtTime(S.trimEnd);
        $('vbrTDur').textContent = fmtTime(S.trimEnd - S.trimStart);
        renderDurationNote();
        refreshSummary();
    }
    function movePlayhead(t) {
        $('vbrHead').style.insetInlineStart = (t / S.duration * 100) + '%';
    }

    /* ═══════════════════════════ engines ═══════════════════════════ */

    /**
     * Snapshot the frame under the playhead — the sample the colour key needs
     * and the "before" side of the compare.
     *
     * ⚠️ `readyState` and a resolved seek are NOT proof that a frame is on the
     * element: drawing a <video> that has not painted yields a BLANK canvas,
     * silently. Measured here — the first version grabbed inside onReady and
     * every consumer downstream then worked perfectly on nothing: autoDetect
     * read a uniform black border, reported a black backdrop with confidence
     * 1.0, and the live preview keyed the whole frame away. So the grab VERIFIES
     * itself (a real frame has some variance) and retries on the next animation
     * frame until it gets one.
     */
    function grabFrame(attempt) {
        var v = player && player.video;
        if (!v || !S.videoW) return;
        attempt = attempt || 0;
        try {
            var w = Math.min(640, S.videoW);
            var h = Math.max(2, Math.round(w * S.videoH / S.videoW));
            if (!previewFrame) previewFrame = document.createElement('canvas');
            if (previewFrame.width !== w || previewFrame.height !== h) { previewFrame.width = w; previewFrame.height = h; }
            var ctx = previewFrame.getContext('2d', { willReadFrequently: true });
            ctx.clearRect(0, 0, w, h);
            ctx.drawImage(v, 0, 0, w, h);

            if (!frameHasContent(previewFrame) && attempt < 12) {
                requestAnimationFrame(function () { grabFrame(attempt + 1); });
                return;
            }
            if (S.engine === 'chroma') { autoKeyOnce(); schedulePreview(); }
        } catch (_) {}
    }

    /** Anything drawn at all: a blank canvas is fully transparent everywhere. */
    function frameHasContent(c) {
        try {
            var d = c.getContext('2d', { willReadFrequently: true })
                .getImageData(0, 0, c.width, c.height).data;
            var step = Math.max(4, Math.floor(d.length / 4 / 900) * 4);
            for (var i = 3; i < d.length; i += step) if (d[i] > 8) return true;
        } catch (_) { return true; }   // tainted or unreadable — assume real
        return false;
    }

    function setupEngine() {
        S.chroma = VbrChroma.defaults();
        pressGroup($('vbrEngine'), function (v) {
            S.engine = v;
            $('vbrEngineHint').textContent = tr('engine_' + v + '_desc');
            $('vbrChromaBox').hidden = v !== 'chroma';
            root.__toolSettings.save(SLUG, 'engine', v);
            ae('engine', { engine: v });
            if (v === 'chroma') { autoKeyOnce(); schedulePreview(); }
            else showCompare(false);
            refreshSummary();
        });

        pressGroup($('vbrChromaBox').querySelector('.vbr-bgrow'), function (k, btn) {
            if (k === 'pick') { startEyedropper(btn); return; }
            if (k === 'dark' || k === 'light') { S.chroma.mode = k; }
            else {
                S.chroma.mode = 'color';
                S.chroma.color = k === 'green' ? [0, 177, 64] : [0, 71, 187];
            }
            applyModeDefaults();
            schedulePreview();
        });

        slider('vbrTol', 'vbrTolV', function (v) { S.chroma.tolerance = v; slidersTouched = true; schedulePreview(); });
        slider('vbrSoft', 'vbrSoftV', function (v) { S.chroma.softness = v; slidersTouched = true; schedulePreview(); });
        slider('vbrSpill', 'vbrSpillV', function (v) { S.chroma.spill = v; schedulePreview(); });

        var saved = root.__toolSettings.load(SLUG, 'engine', 'ai');
        if (saved === 'chroma') {
            press($('vbrEngine'), 'chroma');
            S.engine = 'chroma';
            $('vbrChromaBox').hidden = false;
            $('vbrEngineHint').textContent = tr('engine_chroma_desc');
        }
    }

    /**
     * A luma key and a colour key do not share a sensible starting point. The
     * shipped 32/22 was tuned for chroma; on a black backdrop it scored F1 0.81
     * and on a white one 0.51 (10-scene ground-truth bench, 2026-09-02), where
     * 8/12 scores 0.98–0.99. So the defaults follow the mode — and only when the
     * person has not moved the sliders themselves.
     */
    var slidersTouched = false;
    function applyModeDefaults() {
        if (slidersTouched) return;
        var luma = S.chroma.mode === 'dark' || S.chroma.mode === 'light';
        var d = luma ? { tolerance: 8, softness: 12 } : { tolerance: 32, softness: 22 };
        S.chroma.tolerance = d.tolerance; S.chroma.softness = d.softness;
        var t = $('vbrTol'), so = $('vbrSoft');
        if (t) { t.value = d.tolerance; $('vbrTolV').textContent = d.tolerance; }
        if (so) { so.value = d.softness; $('vbrSoftV').textContent = d.softness; }
    }

    function slider(id, outId, fn) {
        var el = $(id), out = $(outId);
        if (!el) return;
        var sync = function () {
            var v = parseFloat(el.value);
            if (out) out.textContent = el.step && el.step !== '1' ? v : Math.round(v);
            fn(v);
        };
        el.addEventListener('input', sync);
        el.__sync = sync;
    }

    function autoKeyOnce() {
        if (autoKeyDone) return;
        if (!previewFrame || !frameHasContent(previewFrame)) return;
        autoKeyDone = true;
        autoKey();
    }

    /** Guess the key from the current frame and say whether it found one. */
    function autoKey() {
        if (!previewFrame) return;
        var d = VbrChroma.autoDetect(previewFrame);
        var hint = $('vbrKeyHint');
        if (d.confidence >= 0.45) {
            S.chroma.mode = d.mode;
            if (d.mode === 'color') S.chroma.color = d.color;
            var key = d.mode === 'dark' ? 'dark' : (d.mode === 'light' ? 'light' : nearestSwatch(d.color));
            press($('vbrChromaBox').querySelector('.vbr-bgrow'), key, 'data-key');
            applyModeDefaults();
            if (hint) hint.textContent = tr('key_hint_found');
            ae('chroma_autodetect', { mode: d.mode, conf: Math.round(d.confidence * 100) });
        } else if (hint) {
            hint.textContent = tr('key_hint_none');
            ae('chroma_autodetect', { mode: 'none', conf: Math.round(d.confidence * 100) });
        }
    }
    function nearestSwatch(rgb) {
        return (rgb[2] > rgb[1] && rgb[2] > rgb[0]) ? 'blue' : 'green';
    }

    /** Eyedropper: the next click on the preview picks the key colour. */
    function startEyedropper(btn) {
        var view = $('vbrViewport');
        if (!view) return;
        view.style.cursor = 'crosshair';
        root.__toast(tr('key_pick_prompt'), 'info');
        var done = function (e) {
            view.style.cursor = '';
            view.removeEventListener('click', done, true);
            var host = $('vbrCompare').hidden ? view : $('vbrCompare');
            var r = host.getBoundingClientRect();
            if (previewFrame && r.width && r.height) {
                autoKeyDone = true;
                S.chroma.mode = 'color';
                S.chroma.color = VbrChroma.sampleAt(previewFrame,
                    (e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height);
                if (btn) btn.style.background = 'rgb(' + S.chroma.color.join(',') + ')';
                ae('chroma_pick', {});
                schedulePreview();
            }
            e.preventDefault(); e.stopPropagation();
        };
        view.addEventListener('click', done, true);
    }

    /* Live colour-key preview. Coalesced: a slider drag fires input events far
       faster than a full-frame key can run, and queueing them all would make
       the control feel heavier the more it is used. */
    function schedulePreview() {
        if (S.engine !== 'chroma' || !previewFrame || state() !== 'edit') return;
        if (previewBusy) { previewAgain = true; return; }
        previewBusy = true;
        var run = function () {
            try {
                var m = VbrChroma.matte(previewFrame, S.chroma);
                m = VbrMatteEdge.apply(m, S.edge);
                drawCompare(previewFrame, m);
                showCompare(true);
            } catch (_) {}
            previewBusy = false;
            if (previewAgain) { previewAgain = false; schedulePreview(); }
        };
        // scheduler.yield() hands the frame back to the browser before the pass,
        // so the slider thumb keeps tracking the pointer instead of stepping.
        if (root.scheduler && root.scheduler.yield) root.scheduler.yield().then(run);
        else setTimeout(run, 0);
    }


    /* ═══════════════════════════ keep an object ═══════════════════════════ */
    /* RobustVideoMatting keeps people only — a piano, a guitar, a desk is
       background to it (measured on real pianist footage, 23.09.2026). The
       person taps the object on the frame; EdgeTAM cuts it, the tracker carries
       it through the clip, and the matte pass unions it with the person. */

    function setupKeep() {
        var box = $('vbrKeep'), btn = $('vbrKeepBtn'), cv = $('vbrKeepCanvas'), reset = $('vbrKeepReset');
        if (!box || !btn || !cv || !root.VbrKeep || !VbrKeep.available()) return;
        box.hidden = false;
        btn.addEventListener('click', function () { if (keep.busy) return; if (keep.mode) finishKeep(); else enterKeep(); });
        reset.addEventListener('click', function () { clearKeep(true); });
        cv.addEventListener('click', onKeepTap);
        // A black piano on a black curtain comes back with a piece of the
        // curtain; the second switch turns the next taps into «not this».
        pressGroup($('vbrKeepMode'), function (v) {
            keep.label = v === '0' ? 0 : 1;
            if (VbrKeep.hasSelection()) setKeepHint(keep.label ? tr('keep_more') : tr('keep_more_sub'));
        });
    }
    function setKeepHint(text) { var h = $('vbrKeepHint'); if (h) h.textContent = text; }
    function keepKey() { return S.limitKey + '|' + S.trimStart.toFixed(2) + '|' + S.trimEnd.toFixed(2); }
    function ensureKeepSamples() {
        if (keep.samplingP && keep.sampledFor === keepKey()) return keep.samplingP;
        keep.sampledFor = keepKey();
        VbrKeep.reset(true);
        var srcP = (root.FileBytes && FileBytes.settle) ? FileBytes.settle(S.file).then(function (f) { return f || S.file; }, function () { return S.file; }) : Promise.resolve(S.file);
        keep.samplingP = srcP.then(function (src) { return VbrKeep.sample({
            file: src, url: S.url, trimStart: S.trimStart, trimEnd: S.trimEnd,
            videoW: S.videoW, videoH: S.videoH,
        }, function (p) {
            if (keep.mode && keep.busy) setKeepHint(tr('keep_loading', { pct: Math.round(p * 100) }));
        }); });
        keep.samplingP.catch(function () { keep.samplingP = null; keep.sampledFor = ''; });
        return keep.samplingP;
    }
    function setKeepButton(active) {
        var btn = $('vbrKeepBtn'), lab = $('vbrKeepBtnLabel');
        if (btn) btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        if (lab) lab.textContent = active ? tr('keep_done') : tr('keep_btn');
    }
    function enterKeep() {
        if (!S.file) return;
        keep.mode = true;
        setKeepButton(true);
        try { if (player && player.video && !player.video.paused) player.video.pause(); } catch (_) {}
        showCompare(false);
        VbrKeep.warm();
        ensureKeepSamples();
        grabFrame();
        var tries = 0;
        (function paint() {
            if (!keep.mode) return;
            if ((!previewFrame || !frameHasContent(previewFrame)) && tries++ < 30) { requestAnimationFrame(paint); return; }
            drawKeep();
            $('vbrKeepCanvas').hidden = false;
        })();
        keep.label = 1;
        press($('vbrKeepMode'), '1');
        $('vbrKeepMode').hidden = !VbrKeep.hasSelection();
        setKeepHint(tr('keep_pick'));
        ae('keep', { step: 'open', engine: S.engine });
    }
    function exitKeep(keepHint) {
        keep.mode = false;
        setKeepButton(false);
        var cv = $('vbrKeepCanvas');
        if (cv) cv.hidden = true;
        var sw = $('vbrKeepMode');
        if (sw) sw.hidden = true;
        if (!keepHint) setKeepHint(tr('keep_hint'));
        if (S.engine === 'chroma') schedulePreview();
    }
    function clearKeep(byUser) {
        if (root.VbrKeep) VbrKeep.reset();
        var r = $('vbrKeepReset');
        if (r) r.hidden = true;
        exitKeep(false);
        if (byUser) ae('keep', { step: 'reset' });
    }
    function keepTime() {
        var v = player && player.video;
        var t = v ? v.currentTime : S.trimStart;
        return Math.max(S.trimStart, Math.min(S.trimEnd, t || 0));
    }
    /* The frame is drawn with object-fit: contain, so a tap is mapped through
       the letterbox the element really shows, not the element's box. */
    function keepPoint(e) {
        var cv = $('vbrKeepCanvas'), r = cv.getBoundingClientRect();
        var sc = Math.min(r.width / cv.width, r.height / cv.height);
        var dw = cv.width * sc, dh = cv.height * sc;
        var ox = r.left + (r.width - dw) / 2, oy = r.top + (r.height - dh) / 2;
        var nx = (e.clientX - ox) / dw, ny = (e.clientY - oy) / dh;
        if (nx < 0 || ny < 0 || nx > 1 || ny > 1) return null;
        return { nx: nx, ny: ny };
    }
    function drawKeep() {
        var cv = $('vbrKeepCanvas');
        if (!cv || !previewFrame) return;
        if (cv.width !== previewFrame.width || cv.height !== previewFrame.height) { cv.width = previewFrame.width; cv.height = previewFrame.height; }
        var c = cv.getContext('2d');
        c.clearRect(0, 0, cv.width, cv.height);
        c.drawImage(previewFrame, 0, 0);
        var pm = VbrKeep.previewMask();
        if (!pm) return;
        var tint = document.createElement('canvas');
        tint.width = pm.w; tint.height = pm.h;
        var tc = tint.getContext('2d'), img = tc.createImageData(pm.w, pm.h), d = img.data;
        for (var i = 0, j = 0; i < pm.mask.length; i++, j += 4) {
            if (!pm.mask[i]) continue;
            d[j] = 59; d[j + 1] = 130; d[j + 2] = 246; d[j + 3] = 120;
        }
        tc.putImageData(img, 0, 0);
        c.imageSmoothingEnabled = true;
        c.drawImage(tint, 0, 0, cv.width, cv.height);
    }
    async function onKeepTap(e) {
        if (!keep.mode || keep.busy) return;
        var pt = keepPoint(e);
        if (!pt) return;
        keep.busy = true;
        setKeepHint(tr('keep_loading', { pct: 0 }));
        try {
            await ensureKeepSamples();
            // «remove» only means something once there is a selection to cut
            var label = VbrKeep.hasSelection() ? keep.label : 1;
            var r = await VbrKeep.tap(pt.nx, pt.ny, keepTime(), label);
            if (r.ok) {
                drawKeep();
                $('vbrKeepMode').hidden = false;
                setKeepHint(keep.label ? tr('keep_more') : tr('keep_more_sub'));
            } else {
                setKeepHint(r.reason === 'model' ? tr('keep_fail_model') : tr('keep_fail_nothing'));
            }
            ae('keep', { step: 'tap', label: label, ok: r.ok ? 1 : 0, reason: r.reason || '', via: r.via || '', ep: r.provider || VbrKeep.provider() || '',
                ms: r.ms || 0, frac: r.frac || 0, iou: r.iou || 0, cands: r.cands ? JSON.stringify(r.cands).slice(0, 120) : '' });
        } catch (err) {
            setKeepHint(tr('keep_fail_model'));
            ae('keep', { step: 'error', where: 'tap', msg: String((err && err.message) || err).slice(0, 120) });
        } finally { keep.busy = false; }
    }
    async function finishKeep() {
        if (!VbrKeep.hasSelection()) { exitKeep(false); return false; }
        keep.busy = true;
        setKeepHint(tr('keep_tracking', { pct: 0 }));
        var ok = false;
        try {
            var r = await VbrKeep.track(function (p) { setKeepHint(tr('keep_tracking', { pct: Math.round(p * 100) })); });
            ok = !!r.ok;
            if (ok) {
                $('vbrKeepReset').hidden = false;
                setKeepHint(r.lost ? tr('keep_lost') : tr('keep_kept'));
            } else setKeepHint(tr('keep_fail_nothing'));
            ae('keep', { step: 'tracked', ok: ok ? 1 : 0, kfs: r.kfs || 0, masks: r.masks || 0, dropped: r.dropped || 0, lost: r.lost || 0, ms: r.ms || 0,
                ep: VbrKeep.provider() || '', dur_s: Math.round(S.trimEnd - S.trimStart) });
        } catch (err) {
            setKeepHint(tr('keep_fail_model'));
            ae('keep', { step: 'error', where: 'track', msg: String((err && err.message) || err).slice(0, 120) });
        } finally {
            keep.busy = false;
            exitKeep(true);
        }
        return ok;
    }

    /* ═══════════════════════════ before / after ═══════════════════════════ */

    function showCompare(on, mode) {
        var cmp = $('vbrCompare'), mount = $('vbrPlayerMount');
        if (!cmp || !mount) return;
        cmp.hidden = !on;
        cmp.dataset.mode = mode || 'split';
        mount.style.display = on ? 'none' : '';
    }

    function drawCompare(before, after) {
        var A = $('vbrCmpA'), B = $('vbrCmpB');
        if (!A || !B) return;
        var w = after.width, h = after.height;
        if (A.width !== w || A.height !== h) { A.width = w; A.height = h; B.width = w; B.height = h; }
        var ac = A.getContext('2d'); ac.clearRect(0, 0, w, h);
        if (before) ac.drawImage(before, 0, 0, w, h);
        var bc = B.getContext('2d'); bc.clearRect(0, 0, w, h); bc.drawImage(after, 0, 0);
        // The clipped layer is positioned absolutely over the base, so the base
        // must define the box: give it a real aspect ratio to occupy — and the
        // ratio as a number, which caps the box's width so a portrait frame fits
        // the stage's height instead of overflowing it (see .vbr-compare CSS).
        var cmp = $('vbrCompare');
        cmp.style.aspectRatio = w + ' / ' + h;
        cmp.style.setProperty('--vbr-ar', (w / h).toFixed(5));
    }

    function setupCompare() {
        var cmp = $('vbrCompare'), handle = $('vbrCmpHandle'), clip = $('vbrCmpClip');
        if (!cmp) return;
        var set = function (pct) {
            pct = Math.max(0, Math.min(100, pct));
            cmp.style.setProperty('--vbr-cmp', pct + '%');
            handle.setAttribute('aria-valuenow', Math.round(pct));
        };
        set(50);
        var dragging = false;
        var at = function (x) {
            var r = cmp.getBoundingClientRect();
            var p = (x - r.left) / r.width * 100;
            return getComputedStyle(cmp).direction === 'rtl' ? 100 - p : p;
        };
        cmp.addEventListener('pointerdown', function (e) {
            dragging = true; set(at(e.clientX));
            try { cmp.setPointerCapture(e.pointerId); } catch (_) {}
        });
        cmp.addEventListener('pointermove', function (e) { if (dragging) set(at(e.clientX)); });
        document.addEventListener('pointerup', function () { dragging = false; });
        handle.addEventListener('keydown', function (e) {
            var cur = parseFloat(handle.getAttribute('aria-valuenow')) || 50;
            if (e.key === 'ArrowLeft') { set(cur - 4); e.preventDefault(); }
            if (e.key === 'ArrowRight') { set(cur + 4); e.preventDefault(); }
        });
    }

    /* ═══════════════════════════ destination ═══════════════════════════ */

    function setupTargets() {
        var box = $('vbrTargets');
        pressGroup(box, function (id) {
            S.target = id;
            $('vbrTargetHint').textContent = tr('target_' + id + '_desc');
            root.__toolSettings.save(SLUG, 'target', id);
            ae('target', { target: id });
            applyTargetConstraints();
            refreshSummary();
        });

        var saved = root.__toolSettings.load(SLUG, 'target', 'overlay');
        var savedBtn = box.querySelector('[data-target="' + saved + '"]');
        if (savedBtn && saved !== 'overlay') savedBtn.click();

        refreshTargetAvailability();
    }

    /**
     * Mark what this browser cannot produce and what the plan does not include —
     * both BEFORE the click, because both used to be found out at the end of an
     * export.
     *
     * Re-runnable, and re-run when a file arrives: the capability probe behind
     * `available()` can fail to come off while the page is still loading, and a
     * target closed on the strength of that must be able to open again. It also
     * only ever moves OFF an unavailable target — never onto one — so it cannot
     * overrule a choice the person made.
     */
    function refreshTargetAvailability() {
        var box = $('vbrTargets');
        if (!box) return;
        VbrPresets.list().forEach(async function (t) {
            var btn = box.querySelector('[data-target="' + t.id + '"]');
            if (!btn) return;
            if (t.pro && !isPro()) addLock(btn, tr('target_pro', { plan: planName() }));
            var av = await VbrPresets.available(t.id);
            if (av.ok) {
                if (btn.disabled) {
                    btn.disabled = false;
                    btn.removeAttribute('aria-disabled');
                    btn.title = '';
                }
                return;
            }
            // "I could not measure it" is not "your browser cannot do it". Only
            // an answer from the browser closes the door; anything else waits.
            if (!av.conclusive) {
                if (availabilityTries < 3) {
                    availabilityTries++;
                    setTimeout(refreshTargetAvailability, 1200);
                }
                return;
            }
            if (btn.disabled) return;
            btn.disabled = true;
            btn.title = tr('target_unavailable');
            btn.setAttribute('aria-disabled', 'true');
            if (btn.getAttribute('aria-pressed') === 'true') {
                var fb = box.querySelector('[data-target="page"]');
                if (fb) fb.click();
            }
            ae('target_unavailable', { target: t.id, reason: av.reason });
        });
    }

    function addLock(btn, title) {
        if (btn.querySelector('.vbr-lock')) return;
        var s = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        s.setAttribute('class', 'vbr-lock');
        s.setAttribute('viewBox', '0 0 24 24');
        s.setAttribute('fill', 'none');
        s.setAttribute('stroke', 'currentColor');
        s.setAttribute('stroke-width', '2.4');
        s.innerHTML = '<rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 018 0v4"/>';
        btn.appendChild(s);
        btn.title = title;
    }

    /**
     * What the chosen destination forces. Two of these were silent bugs before:
     * an opaque target with a transparent background produced a file with the
     * subject burned onto nothing, and a target with size/fps/audio rules
     * applied them without saying so.
     */
    function applyTargetConstraints() {
        var t = VbrPresets.get(S.target);
        var needsBg = VbrPresets.requiresBackground(S.target);
        var sec = $('vbrBgSec');

        if (sec) {
            sec.hidden = !needsBg;
            // An opaque target cannot mean "transparent"; move off it rather than
            // letting the pair exist.
            if (needsBg && S.bgMode === 'transparent') {
                var first = $('vbrBgRow').querySelector('[data-bg="color"]');
                if (first) first.click();
            }
        }
        // A destination that forbids audio must not show an audio switch.
        var aw = $('vbrAudioWrap');
        if (aw) aw.hidden = (t.audio === false);

        var fit = VbrPresets.fit(S.target, {
            w: S.videoW, h: S.videoH, fps: S.fps,
            duration: S.trimEnd - S.trimStart, hasAudio: S.hasAudio,
        });
        S.clamped = fit.clamped;
        renderClampNote(fit);
    }

    function renderClampNote(fit) {
        var hint = $('vbrTargetHint');
        if (!hint) return;
        var base = tr('target_' + S.target + '_desc');
        if (!fit.clamped.length) { hint.textContent = base; return; }
        var parts = fit.clamped.map(function (c) {
            if (c === 'size') return tr('clamped_size', { w: fit.w, h: fit.h });
            if (c === 'fps') return tr('clamped_fps', { fps: fit.fps });
            if (c === 'duration') return tr('clamped_duration', { s: fit.duration });
            if (c === 'audio') return tr('clamped_audio');
            return '';
        }).filter(Boolean);
        hint.textContent = base + ' ' + parts.join(' ');
    }

    /* ═══════════════════════════ background ═══════════════════════════ */

    function setupBackground() {
        pressGroup($('vbrBgRow'), function (mode, btn) {
            if (mode === 'color') { S.bgMode = 'color'; S.bgColor = btn.dataset.color; }
            else S.bgMode = mode;
            $('vbrBlurRow').hidden = mode !== 'blur';
            if (mode === 'image' && !S.bgImage) $('vbrBgImg').click();
            refreshSummary();
        });
        $('vbrBgImg').addEventListener('change', function () {
            var f = this.files && this.files[0];
            if (!f) return;
            var img = new Image();
            img.onload = function () {
                S.bgImage = img; S.bgMode = 'image';
                var b = $('vbrBgRow').querySelector('[data-bg="image"]');
                if (b) {
                    b.style.backgroundImage = 'url(' + img.src + ')';
                    b.style.backgroundSize = 'cover';
                    press($('vbrBgRow'), 'image', 'data-bg');
                }
                refreshSummary();
            };
            img.src = URL.createObjectURL(f);
        });
        $('vbrBgCustom').addEventListener('input', function () {
            S.bgMode = 'color'; S.bgColor = this.value;
            var lbl = this.closest('.vbr-bgbtn');
            if (lbl) lbl.style.background = this.value;
            press($('vbrBgRow'), 'custom', 'data-bg');
            refreshSummary();
        });
        slider('vbrBlur', 'vbrBlurV', function (v) { S.blurRadius = v; });
    }

    function bgConfig() {
        // A transparent-capable destination ALWAYS exports the alpha: choosing a
        // background there would throw away the one thing that target is for.
        if (VbrPresets.allowsTransparency(S.target)) return { mode: 'transparent' };
        return {
            mode: S.bgMode === 'transparent' ? 'color' : S.bgMode,
            color: S.bgColor,
            grad: { type: S.gradType, angle: S.gradAngle, c1: S.gradC1, c2: S.gradC2 },
            image: S.bgImage,
            blurRadius: S.blurRadius,
        };
    }

    /* ═══════════════════════════ advanced ═══════════════════════════ */

    function setupAdvanced() {
        pressGroup($('vbrRes'), function (v) {
            S.resHeight = parseInt(v, 10);
            root.__toolSettings.save(SLUG, 'res', S.resHeight);
            refreshSummary();
        });
        pressGroup($('vbrFps'), function (v) {
            S.fps = parseInt(v, 10);
            $('vbrFpsHint').textContent = tr('fps_' + v + '_desc');
            root.__toolSettings.save(SLUG, 'fps', S.fps);
            applyTargetConstraints();
            refreshSummary();
        });
        slider('vbrFeather', 'vbrFeatherV', function (v) { S.edge.feather = v; schedulePreview(); refreshSummary(); });
        slider('vbrChoke', 'vbrChokeV', function (v) { S.edge.choke = v; schedulePreview(); refreshSummary(); });

        $('vbrAudio').addEventListener('change', function () {
            S.audioKeep = this.checked;
            $('vbrAudioLabel').textContent = tr(this.checked ? 'audio_keep' : 'audio_remove');
        });

        var sf = parseInt(root.__toolSettings.load(SLUG, 'fps', 15), 10);
        if (sf === 24 || sf === 30) { press($('vbrFps'), sf); S.fps = sf; }
        var sr = parseInt(root.__toolSettings.load(SLUG, 'res', 720), 10);
        if (sr && sr !== 720) {
            var b = $('vbrRes').querySelector('[data-val="' + sr + '"]');
            if (b && !b.disabled) { press($('vbrRes'), sr); S.resHeight = sr; }
        }
    }

    /**
     * Resolution options come from the SOURCE, not from a fixed list.
     *
     * Matting adds no detail, so an option above the clip's own height would be
     * an upscale sold as a choice — and the plan line beside the chips reads
     * "Премиум: up to 2160p", which is true of the axis and false of a 640×360
     * clip. Offering only steps the footage can actually fill keeps the two
     * honest at once. __qualityTier re-gates on mutation, so the ceiling lands
     * on the rebuilt chips without being asked.
     */
    function buildResChips() {
        var box = $('vbrRes');
        if (!box || !S.videoH) return;
        var src = S.videoH;
        var steps = [360, 480, 720, 1080, 1440, 2160].filter(function (h) { return h < src; });
        steps.push(src);                                  // the clip's own height
        steps = steps.slice(-4);                          // keep the row to four
        var want = Math.min(S.resHeight, steps[steps.length - 1]);
        var best = steps.reduce(function (a, b) { return Math.abs(b - want) < Math.abs(a - want) ? b : a; }, steps[0]);

        box.innerHTML = '';
        steps.forEach(function (h) {
            var b = document.createElement('button');
            b.type = 'button';
            b.setAttribute('data-val', h);
            b.setAttribute('data-h', h);
            b.setAttribute('aria-pressed', String(h === best));
            b.textContent = (h === src) ? tr('res_original') : (h + 'p');
            box.appendChild(b);
        });
        S.resHeight = best;

        /* Gate — and disclose — only where the plan actually closes an option.
           A 360p clip offers nothing above the 720p ceiling, and the plan line
           there would read "your plan exports 720p" beside a file that will
           export at 360: true about the tier, false about this export, and the
           only thing on screen that looked like a promise. */
        var ceiling = maxHeight();
        if (steps[steps.length - 1] > ceiling) {
            box.setAttribute('data-quality-gate-group', 'height');
            box.setAttribute('data-qt-attr', 'data-h');
            try { root.__qualityTier && root.__qualityTier.gateGroup(box, 'height', 'data-h'); } catch (_) {}
        } else {
            box.removeAttribute('data-quality-gate-group');
        }
        refreshSummary();
    }

    /** One line that states everything the advanced sheet holds, so the sheet
        does not have to be opened to know what will come out. */
    function refreshSummary() {
        var el = $('vbrSummary');
        if (!el) return;
        var d = outputDims();
        var t = VbrPresets.get(S.target);
        var fit = VbrPresets.fit(S.target, { w: d.w, h: d.h, fps: S.fps, duration: S.trimEnd - S.trimStart, hasAudio: S.hasAudio });
        el.innerHTML = '<b>' + fit.w + '×' + fit.h + '</b> · ' + fit.fps + ' fps · '
            + String(t.ext).toUpperCase()
            + (S.edge.feather || S.edge.choke ? ' · ' + tr('edge_title').toLowerCase() : '');
    }

    /**
     * Working resolution. Bounded by three things at once and by the SMALLEST of
     * them: the source (matting invents no detail), the plan ceiling, and the
     * pipeline's own 1920 long edge. The plan ceiling is applied here as well as
     * on the chip row because a chip is a request and this is the encode.
     */
    function outputDims() {
        var w = S.videoW, h = S.videoH;
        if (!w || !h) return { w: 0, h: 0 };
        var ceil = Math.min(S.resHeight, maxHeight());
        if (h > ceil) { var k = ceil / h; w = Math.round(w * k); h = Math.round(h * k); }
        var long = Math.max(w, h);
        if (long > 1920) { var k2 = 1920 / long; w = Math.round(w * k2); h = Math.round(h * k2); }
        if (w % 2) w--;
        if (h % 2) h--;
        return { w: Math.max(2, w), h: Math.max(2, h) };
    }

    /* ═══════════════════════════ matte pass ═══════════════════════════ */

    /* One matte pass at a time. The button stays clickable until the phase
       swap lands (a view transition), and a double press started two recurrent
       RVM passes on ONE session — onnxruntime answers «Session already started»
       and the frame source filed it as a decoder failure (prod, 3 sessions). */
    var matteStarting = false;
    async function startMatte() {
        if (matteStarting) return;
        matteStarting = true;
        try { await startMatteOnce(); } finally { matteStarting = false; }
    }
    async function startMatteOnce() {
        if (!S.file) return;
        // A selection the person made but did not confirm is still their intent:
        // carry it through the clip before the matte pass, not silently drop it.
        if (keep.busy) return;
        if (root.VbrKeep && (keep.mode || (VbrKeep.hasSelection() && !VbrKeep.active()))) await finishKeep();
        /* Refuse BEFORE the work, not after it. A tool whose run costs minutes
           must answer the cap at the press, otherwise the person pays the wait
           and then gets the wall. Key = the file, so a second attempt on the
           same clip today (a different destination, a re-key) is free — which is
           what the wall itself promises. */
        limitRun = root.__toolLimit ? root.__toolLimit.begin(SLUG, [S.limitKey]) : { commit: function () {}, abort: function () {} };
        if (!limitRun) { ae('capped', {}); return; }

        var d = VbrPresets.fit(S.target, {
            w: outputDims().w, h: outputDims().h, fps: S.fps,
            duration: S.trimEnd - S.trimStart, hasAudio: S.hasAudio,
        });
        S.processW = d.w; S.processH = d.h;
        if (d.duration && d.duration < S.trimEnd - S.trimStart) S.trimEnd = S.trimStart + d.duration;

        abortCtrl = new AbortController();
        matteDone = false;

        /* The durable copy the uploader already made, not the live handle: on
           Android the picker's permission lapses mid-run and every later slice
           read throws NotReadableError («The requested file could not be
           read…» — 20 sessions on RU in two weeks, all of them after the
           matte pass had started). settle() never starts a read of its own. */
        var srcFile = S.file;
        try { if (root.FileBytes && FileBytes.settle) srcFile = (await FileBytes.settle(S.file)) || S.file; } catch (_) {}
        // the export reads the source again (a blur's frames, the sound): the same copy
        S.srcFile = srcFile;

        VbrPipeline.prepare({
            file: srcFile, url: S.url, fps: d.fps,
            engine: S.engine, chroma: S.chroma, edge: S.edge,
            trimStart: S.trimStart, trimEnd: S.trimEnd,
            processW: S.processW, processH: S.processH,
            videoW: S.videoW, videoH: S.videoH, fileName: S.fileName,
            bg: bgConfig(), format: VbrPresets.get(S.target).format,
            keepAudio: S.audioKeep && VbrPresets.get(S.target).audio !== false && S.hasAudio,
            abortSignal: abortCtrl.signal,
        });

        setState('work');
        stage('processing');
        ae('start', {
            engine: S.engine, target: S.target, fps: d.fps,
            h: S.processH, dur_s: Math.round(S.trimEnd - S.trimStart),
            keep: root.VbrKeep && VbrKeep.active() ? 1 : 0,
        });
        setProgress(2,
            S.engine === 'chroma' ? tr('loading_key') : tr('loading_model'),
            S.engine === 'chroma' ? '' : tr('loading_model_first'));

        var live = $('vbrLive'), lc = $('vbrLiveCanvas');
        live.hidden = false;
        lc.width = S.processW; lc.height = S.processH;
        var lctx = lc.getContext('2d');

        try {
            if (S.engine === 'ai') {
                VbrRvmEngine.onModelProgress(function (loaded, total) {
                    if (total) setProgress(2 + Math.round(loaded / total * 20), tr('loading_model'),
                        fmtBytes(loaded) + ' / ' + fmtBytes(total));
                });
            }
            S.matteStats = await VbrPipeline.mattePass({
                onFramePreview: function (m) {
                    lctx.clearRect(0, 0, lc.width, lc.height);
                    lctx.drawImage(m, 0, 0, lc.width, lc.height);
                },
                onProgress: function (phase, pct, info) {
                    setProgress(22 + Math.round(pct * 76),
                        tr('processing_frame', { current: info.frame, total: info.total }),
                        info.eta ? tr('processing_eta', { time: fmtETA(info.eta) }) : '');
                },
            });
            matteDone = true;
            setProgress(100, tr('done'), '');
            enterReview();
        } catch (e) { handleError(e); }
    }

    /* ═══════════════════════════ review ═══════════════════════════ */

    function stopReviewLoop() {
        if (reviewTimer) { clearInterval(reviewTimer); reviewTimer = 0; }
    }

    async function enterReview() {
        setState('review');
        stage('review');
        var meta = VbrMatteStore.meta();
        var total = VbrMatteStore.count();
        var i = 0, busy = false;

        /* Split when the store kept source thumbnails (it does whenever the
           pass handed it the frame), solo otherwise. The thumbnail is 240 px
           wide and drawn under a full-size matte — soft, but the person and the
           layout are recognisable, and that is what "before" is for here:
           measured +3.9 ms/frame and 4.7 KB/frame to keep (2026-09-02). */
        var firstThumb = await VbrMatteStore.getThumb(0);
        var split = !!firstThumb;
        if (firstThumb) firstThumb.close();
        showCompare(true, split ? 'split' : 'solo');

        stopReviewLoop();
        reviewTimer = setInterval(async function () {
            if (busy) return;
            busy = true;
            try {
                var m = await VbrMatteStore.get(i);
                if (m) {
                    var before = split ? await VbrMatteStore.getThumb(i) : null;
                    /* «Стало» is the frame the file will hold — the background
                       the rail has chosen RIGHT NOW (a change shows on the next
                       tick), through the export's own call. A blur is made of
                       the source frame: without a thumbnail there is nothing to
                       blur, and the bare cutout says less wrong than black. */
                    var bg = bgConfig();
                    var shown = (bg.mode === 'blur' && !before)
                        ? VbrPipeline.refine(i, m)
                        : VbrPipeline.frame(i, m, bg, before);
                    drawCompare(before, shown);
                    if (before) before.close();
                }
            } catch (_) {}
            i = (i + 1) % Math.max(1, total);
            busy = false;
        }, 1000 / Math.min(meta.fps || 15, 15));

        // the pass that just ran: its per-frame cost and lane, once
        var ms = S.matteStats; S.matteStats = null;
        ae('review', ms ? { frames: total, ms_frame: ms.msPerFrame || 0, ep: ms.provider || '' } : { frames: total });
        try { root.__announce && root.__announce(tr('result_frames', { n: total })); } catch (_) {}
    }

    /* ═══════════════════════════ export ═══════════════════════════ */

    async function startExport() {
        stopReviewLoop();
        abortCtrl = new AbortController();
        var t = VbrPresets.get(S.target);
        /* The frame rate the STORE was built at, not the chip. A destination
           may have fitted it (Telegram forces 30), and the matte pass ran at
           the fitted value; encoding those frames at the chip's 15 fps produced
           a 5.93-second "3-second" sticker (measured 2026-09-02). */
        var meta = VbrMatteStore.meta() || {};
        var fps = meta.fps || S.fps;
        VbrPipeline.prepare({
            file: S.srcFile || S.file, url: S.url, fps: fps,
            engine: S.engine, chroma: S.chroma, edge: S.edge,
            trimStart: S.trimStart, trimEnd: S.trimEnd,
            processW: S.processW, processH: S.processH,
            videoW: S.videoW, videoH: S.videoH, fileName: S.fileName,
            bg: bgConfig(), format: t.format,
            keepAudio: S.audioKeep && t.audio !== false && S.hasAudio,
            abortSignal: abortCtrl.signal,
        });
        setState('work');
        $('vbrLive').hidden = true;
        setProgress(4, tr('processing_encoding'), tr('processing_prepare'));
        try {
            var res = await VbrPipeline.exportPass({
                onProgress: function (phase, pct) { setProgress(4 + Math.round(pct * 95), tr('processing_encoding'), ''); },
            });
            setProgress(100, tr('done'), '');
            S.resultBlob = res.blob;
            S.resultExt = res.ext;
            if (S.resultUrl) URL.revokeObjectURL(S.resultUrl);
            S.resultUrl = URL.createObjectURL(res.blob);
            /* The unit is spent on the file that was HANDED OVER, so the commit
               belongs here — after a blob exists, before it is offered. */
            if (limitRun) { try { limitRun.commit(); } catch (_) {} limitRun = null; }
            showResult();
        } catch (e) { handleError(e, true); }
    }

    function showResult() {
        setState('done');
        stage('complete');
        var isImage = S.resultExt === 'png' || S.resultExt === 'webp';
        var mount = $('vbrResultMount');
        if (resultPlayer) { resultPlayer.destroy(); resultPlayer = null; }
        mount.innerHTML = '';
        if (isImage) {
            var img = document.createElement('img');
            img.src = S.resultUrl;
            img.alt = '';
            img.style.cssText = 'display:block;max-width:100%;height:auto;margin-inline:auto';
            mount.appendChild(img);
        } else {
            resultPlayer = root.__createTimbricaPlayer(mount, {
                speed: true, volume: true, fullscreen: true, maxHeight: 420,
                onReady: function (p) { p.play(); },
                onEnded: function () { resultPlayer.seek(0); resultPlayer.play(); },
            });
            resultPlayer.load(S.resultUrl);
        }
        var t = VbrPresets.get(S.target);
        var over = t.maxBytes && S.resultBlob.size > t.maxBytes;
        $('vbrWorkS').textContent = '';
        var storeMeta = VbrMatteStore.meta() || {};
        var note = tr('result_meta', {
            w: S.processW, h: S.processH, fps: storeMeta.fps || S.fps,
            dur: fmtTime(S.trimEnd - S.trimStart),
            ext: String(S.resultExt).toUpperCase(),
            size: fmtBytes(S.resultBlob.size),
        });
        $('vbrTargetHint').textContent = note;
        try { root.__announce && root.__announce(note); } catch (_) {}
        ae('done', {
            target: S.target, engine: S.engine, ext: S.resultExt, bg: bgConfig().mode,
            kb: Math.round(S.resultBlob.size / 1024),
            over_target_limit: over ? 1 : 0,
        });
        if (over) root.__toast(tr('clamped_size', { w: S.processW, h: S.processH }), 'warning');
    }

    function setupResult() {
        /* Back from the result to the review — the matte stays in the store, so
           a new background or destination is one more «Собрать файл», not a
           second matte pass; the unit was spent on this file already, and the
           same file today is free. Until 02.10.2026 the result screen had only
           «Скачать» and «Другое видео»: a blur radius that came out wrong meant
           uploading the clip again and waiting for the whole network pass. */
        $('vbrRedo').addEventListener('click', function () {
            if (!matteDone) return;
            if (resultPlayer) { resultPlayer.destroy(); resultPlayer = null; }
            ae('redo', { target: S.target, bg: bgConfig().mode });
            applyTargetConstraints();     // the hint line held the result's size — back to the destination's
            enterReview();
        });
        $('vbrSave').addEventListener('click', function () {
            if (!S.resultBlob) return;
            var base = (S.fileName || 'video').replace(/\.[^.]+$/, '');
            var name = base + '-no-bg.' + S.resultExt;
            if (root.__outputName && root.__outputName.sanitize) {
                try { name = root.__outputName.sanitize(name); } catch (_) {}
            }
            if (root.__downloadFile) root.__downloadFile(S.resultBlob, name);
            try {
                root.__aeToolComplete && root.__aeToolComplete({
                    format: S.resultExt, target: S.target, engine: S.engine,
                    sizeMB: Math.round(S.resultBlob.size / 1048576),
                });
            } catch (_) {}
        });
    }

    /* ═══════════════════════════ errors ═══════════════════════════ */

    function handleError(e, fromExport) {
        if (limitRun) { try { limitRun.abort(); } catch (_) {} limitRun = null; }
        if (e && e.name === 'AbortError') {
            if (matteDone) enterReview(); else setState('edit');
            ae('cancelled', { phase: fromExport ? 'export' : 'matte' });
            return;
        }
        var msg = String((e && e.message) || e || '').toLowerCase();
        var key = 'err_processing';
        if (msg.indexOf('webassembly') >= 0) key = 'err_browser';
        else if (msg.indexOf('decode') >= 0) key = 'err_decode';
        else if (msg.indexOf('memory') >= 0 || msg.indexOf('oom') >= 0) key = 'err_memory';
        else if (msg.indexOf('rvm') >= 0 || msg.indexOf('onnx') >= 0 || msg.indexOf('model') >= 0) key = 'err_model';
        else if (msg.indexOf('ffmpeg') >= 0 || msg.indexOf('encode') >= 0 || msg.indexOf('exit') >= 0) key = 'err_export';
        else if (msg.indexOf('fetch') >= 0 || msg.indexOf('load') >= 0) key = 'err_load';
        root.__toast(tr(key), 'error');
        stage('error');
        try { root.__aeCritical && root.__aeCritical('pipeline', { key: key, phase: fromExport ? 'export' : 'matte', msg: msg.slice(0, 140) }); } catch (_) {}
        if (matteDone) enterReview(); else setState('edit');
    }

    /* ═══════════════════════════ init ═══════════════════════════ */

    function init(slug, labels) {
        SLUG = slug || SLUG;
        T = labels || {};
        setupUpload();
        setupTrim();
        setupEngine();
        setupCompare();
        setupTargets();
        setupBackground();
        setupAdvanced();
        setupResult();
        setupKeep();

        $('vbrGo').addEventListener('click', startMatte);
        $('vbrExport').addEventListener('click', startExport);
        $('vbrAgain').addEventListener('click', resetToUpload);
        $('vbrCancel').addEventListener('click', function () {
            // while the source is still being prepared, Cancel stops THAT
            if (S.loading) { resetToUpload(); return; }
            if (abortCtrl) abortCtrl.abort();
            /* The abort lands when the network finishes the frame in flight —
               seconds on a phone. Answer the press NOW: 31 sessions on RU
               pressed «Отмена» and saw nothing change (silent_click, 3 weeks). */
            var b = $('vbrCancel');
            b.disabled = true;
            b.textContent = tr('cancelling');
        });
        $('vbrBack').addEventListener('click', function () {
            stopReviewLoop(); showCompare(S.engine === 'chroma'); setState('edit');
        });
        $('vbrRefine').addEventListener('click', function () {
            stopReviewLoop();
            setState('roto');
            VbrRotoEditor.open(function () { enterReview(); });
        });

        VbrRotoEditor.mount($('vbrRotoMount'), tr);

        // The tier snapshot arrives asynchronously; every ceiling this file
        // shows must be re-rendered when it does, or a Премиум visitor spends
        // the first seconds looking at the guest's numbers.
        var reread = function () {
            renderDurationNote();
            var chip = $('vbrDurChip');
            if (chip) chip.textContent = tr('dur_free', { cap: durationCap() });
            if (S.duration && S.trimEnd - S.trimStart > durationCap()) {
                S.trimEnd = S.trimStart + durationCap();
                updateTrim();
            }
            VbrPresets.list().forEach(function (t) {
                if (!t.pro) return;
                var b = $('vbrTargets').querySelector('[data-target="' + t.id + '"]');
                if (!b) return;
                if (isPro()) { var l = b.querySelector('.vbr-lock'); if (l) l.remove(); }
                else addLock(b, tr('target_pro', { plan: planName() }));
            });
            refreshSummary();
        };
        document.addEventListener('tier-bootstrap:ready', reread);
        window.addEventListener('tier-bootstrap:ready', reread);
        setTimeout(reread, 1800);

        stage('idle');
    }

    root.VbrUI = { init: init, __state: S };
})(typeof window !== 'undefined' ? window : globalThis);
